import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../SEOHead';
import RelatedLinks from './RelatedLinks';

const ForWordPress = () => {
  return (
    <>
    <SEOHead
      title="WordPress Website Feedback Plugin"
      description="Leave visual feedback directly on your WordPress site. One click plugin install. Notes, bugs, and design feedback, invisible to visitors."
      path="/for/wordpress"
    />
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="border-b border-ink-faint/20 bg-cream/80 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="text-base font-display font-semibold tracking-tight text-ink">Cayda</Link>
          <Link to="/sign-up" className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-display font-medium bg-accent text-white hover:bg-accent-hover rounded-lg transition-all">Get started free</Link>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-3xl mx-auto px-6 pt-16 pb-12">
        <p className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-4">Cayda for WordPress</p>
        <h1 className="text-3xl sm:text-4xl tracking-tight leading-tight mb-5">
          <span className="font-serif italic text-ink/80">Website feedback tool</span>{' '}
          <span className="font-display font-bold text-ink">built for WordPress sites.</span>
        </h1>
        <p className="text-base font-display text-ink-secondary leading-relaxed max-w-xl">
          Install the Cayda plugin, and your team can leave notes directly on your WordPress site. Click on any element, a heading, an image, a button, and pin feedback right there. Visitors never see a thing.
        </p>
      </div>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 pb-20">
        <div className="space-y-14 text-[15px] font-display text-ink-secondary leading-relaxed">

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">One click install</h2>
            <p className="mb-3">
              Cayda generates a custom WordPress plugin for each of your sites. Download the .zip from your Cayda dashboard, upload it to Plugins, Add New in WordPress, and activate. That's it. The script is automatically injected into every page of your site.
            </p>
            <p>
              No code to paste. No theme files to edit. No child theme required. The plugin works with any WordPress theme: classic, block based, full site editing, or hybrid.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">Automatic WordPress authentication</h2>
            <p className="mb-3">
              The Cayda WordPress plugin supports automatic authentication using HMAC based verification. When a logged in WordPress user visits your site, Cayda can recognize them automatically. No separate login required.
            </p>
            <p>
              This means your WordPress admins and editors can start leaving feedback the moment they visit the front end of the site. They don't need to create a separate Cayda account or go through an additional login flow.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">Why not just use WordPress comments?</h2>
            <p className="mb-3">
              WordPress comments are for visitors. They're public, they live at the bottom of posts, and they're not designed for internal team feedback on layout, design, or functionality.
            </p>
            <p>
              Cayda annotations are pinned to specific elements on the page, not appended to a comment thread. They're invisible to visitors. They capture screenshots, browser info, and viewport dimensions automatically. And they feed into a task management dashboard where you can assign, prioritize, and track resolution.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">Works with every theme and builder</h2>
            <p className="mb-3">
              Cayda works with Elementor, Divi, Beaver Builder, GeneratePress, Astra, Kadence, and every other WordPress theme and page builder. It doesn't interfere with your theme's CSS or JavaScript. It doesn't add visible elements to your pages. The script loads asynchronously and has zero impact on your site's performance.
            </p>
            <p>
              For WooCommerce sites, annotations work on product pages, the cart, checkout, and any custom template. Your team can leave feedback on pricing display, layout issues, or broken elements, all without affecting the shopping experience.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-4">What you can do</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { title: 'Pin notes to any element', desc: 'Click on headers, images, buttons, forms — any visible element on any page.' },
                { title: 'Categorize by type', desc: 'Mark notes as bugs, design issues, content changes, questions, or general feedback.' },
                { title: 'Auto capture context', desc: 'Every note includes a screenshot, the page URL, browser, OS, and viewport size.' },
                { title: 'Assign & track', desc: 'Assign notes to team members. Set priority. Update status as issues are resolved.' },
                { title: 'Thread replies', desc: 'Discuss issues in threaded comments, right on the page where the issue lives.' },
                { title: 'Push to Slack & Jira', desc: 'New notes trigger Slack notifications. One click creates a Jira issue with full context.' },
              ].map(({ title, desc }) => (
                <div key={title} className="bg-white border border-ink-faint/20 rounded-xl p-4">
                  <p className="text-sm font-display font-semibold text-ink mb-1">{title}</p>
                  <p className="text-xs font-display text-ink-muted leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">Multisite and staging</h2>
            <p>
              Each WordPress site gets its own Cayda plugin with a unique embed token. You can install Cayda on your production site and your staging environment separately, keeping notes organized per environment. On WordPress Multisite, install the plugin on each subsite that needs feedback. Each connects to its own Cayda website entry.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">Performance</h2>
            <p>
              The Cayda script loads asynchronously via a deferred script tag and only activates when an authenticated team member is present. It doesn't add DOM elements for regular visitors. It doesn't set cookies. It has no meaningful impact on your Core Web Vitals or Lighthouse score.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">Pricing</h2>
            <p>
              The plugin is free. Cayda's free plan includes 2 websites, 50 notes per site, and 3 team members with no credit card required. Pro is $9/month base plus $5 per member (billed annually) for unlimited sites, notes, file attachments, and Slack integration. Team is $14/month base plus $6 per member (billed annually) and adds screen recording, guest access, and Jira/Asana integrations. Running a Shopify store instead? Cayda also has a native Shopify app with the same one click, no code install.
            </p>
          </section>
        </div>

        <div className="mt-14"><RelatedLinks current="/for/wordpress" /></div>

        {/* CTA */}
        <div className="mt-16 pt-12 border-t border-ink-faint/20 text-center">
          <h3 className="text-xl font-display font-bold text-ink tracking-tight mb-2">Add feedback to your WordPress site.</h3>
          <p className="text-sm font-display text-ink-muted mb-5">Create an account, download the plugin, and start leaving notes in under two minutes.</p>
          <Link to="/sign-up" className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-display font-medium bg-accent text-white hover:bg-accent-hover rounded-lg transition-all">Get started free →</Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-ink-faint/20 bg-cream">
        <div className="max-w-3xl mx-auto px-6 py-8 flex items-center justify-between">
          <p className="text-xs font-display text-ink-faint">© {new Date().getFullYear()} Slowfast LLC</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="text-xs font-display text-ink-faint hover:text-ink-secondary transition-colors">Privacy</Link>
            <Link to="/terms" className="text-xs font-display text-ink-faint hover:text-ink-secondary transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default ForWordPress;
